import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  dataTableFootClass,
  dataTableHeadClass,
  DataTablePanel,
  PageShell,
} from '../../components/DataTablePanel';
import { FilterField, ListFiltersBar } from '../../components/ListFiltersBar';
import { SortableTh, sortItems, useTableSort } from '../../components/SortableTh';
import {
  Badge,
  Callout,
  Card,
  ListEmptyState,
  LoadingSpinner,
  NumberInput,
  Select,
  Switch,
} from '../../components/ui';
import { ApiError, api } from '../../services/api';
import { t } from '../../i18n';
import { CustomerLookupItem } from '../../types/customer';
import { AgreementVarianceReport } from '../../types/reports';
import { formatMoney } from '../../utils/format';
import { ReportExportButtons } from './ReportExportButtons';
import { exportAgreementVariance, yesNo } from './exportReports';

const totalsCellClass = 'sticky bottom-0 z-10 bg-surface-muted px-3 py-2';
const totalsNumClass = `${totalsCellClass} whitespace-nowrap text-right tabular-nums`;

const sortGetters: Record<
  string,
  (
    row: AgreementVarianceReport['data'][number],
  ) => string | number | boolean | null | undefined
> = {
  customerName: (r) => r.customerName,
  agreement: (r) => r.agreement.total,
  expected: (r) => r.expected.total,
  billed: (r) => r.billed.total,
  varianceInvoice: (r) => r.variance.invoice,
  varianceCash: (r) => r.variance.cash,
  variance: (r) => r.variance.total,
};

export function AgreementVariancePage() {
  const labels = t().reports;
  const now = new Date();
  const [customers, setCustomers] = useState<CustomerLookupItem[]>([]);
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState<number | ''>('');
  const [customerId, setCustomerId] = useState('');
  const [activeOnly, setActiveOnly] = useState(true);
  const [varianceFilter, setVarianceFilter] = useState<
    '' | 'positive' | 'zero' | 'negative' | 'nonzero'
  >('nonzero');
  const [report, setReport] = useState<AgreementVarianceReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { sort, toggleSort } = useTableSort('variance', 'desc');

  const displayRows = useMemo(
    () => (report ? sortItems(report.data, sort, sortGetters) : []),
    [report, sort],
  );

  useEffect(() => {
    void api
      .getCustomerLookup({ active: 'true' })
      .then(setCustomers);
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const data = await api.getAgreementVarianceReport({
          year,
          month: month === '' ? undefined : month,
          customerId: customerId ? Number(customerId) : undefined,
          activeOnly,
          varianceFilter,
        });
        if (!cancelled) setReport(data);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : t().common.error);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [year, month, customerId, activeOnly, varianceFilter]);

  function handleExport(format: 'excel' | 'pdf') {
    if (!report) return;
    const varianceLabel =
      varianceFilter === 'nonzero'
        ? labels.varianceNonzero
        : varianceFilter === 'positive'
          ? labels.variancePositive
          : varianceFilter === 'zero'
            ? labels.varianceZero
            : varianceFilter === 'negative'
              ? labels.varianceNegative
              : labels.varianceAll;
    exportAgreementVariance({
      report,
      rows: displayRows,
      meta: [
        { label: labels.year, value: String(year) },
        {
          label: labels.month,
          value:
            month === ''
              ? labels.allMonths
              : t().common.months[month - 1] ?? String(month),
        },
        {
          label: t().common.customer,
          value:
            customers.find((c) => String(c.id) === customerId)?.name ??
            labels.allCustomers,
        },
        { label: labels.variance, value: varianceLabel },
        { label: labels.activeOnly, value: yesNo(activeOnly) },
      ],
      format,
    });
  }

  return (
    <PageShell>
      <ListFiltersBar
        actions={
          <ReportExportButtons
            disabled={!report}
            onExcel={() => handleExport('excel')}
            onPdf={() => handleExport('pdf')}
          />
        }
      >
        <FilterField label={labels.year}>
          <NumberInput
            value={year}
            allowDecimals={false}
            showClearButton={false}
            size="sm"
            onChange={(e) => {
              if (e.target.value != null) setYear(e.target.value);
            }}
          />
        </FilterField>
        <FilterField label={labels.month}>
          <Select
            value={month === '' ? '' : month}
            size="sm"
            showClearButton={false}
            options={[
              { value: '', label: labels.allMonths },
              ...t().common.months.map((name, index) => ({
                value: index + 1,
                label: name,
              })),
            ]}
            onChange={(e) => {
              const v = e.target.value;
              setMonth(v === '' ? '' : Number(v));
            }}
          />
        </FilterField>
        <FilterField label={t().common.customer}>
          <Select
            value={customerId}
            size="sm"
            options={[
              { value: '', label: labels.allCustomers },
              ...customers.map((c) => ({ value: String(c.id), label: c.name })),
            ]}
            onChange={(e) => setCustomerId(e.target.value)}
          />
        </FilterField>
        <FilterField label={labels.variance}>
          <Select
            value={varianceFilter}
            size="sm"
            showClearButton={false}
            options={[
              { value: '', label: labels.varianceAll },
              { value: 'nonzero', label: labels.varianceNonzero },
              { value: 'positive', label: labels.variancePositive },
              { value: 'zero', label: labels.varianceZero },
              { value: 'negative', label: labels.varianceNegative },
            ]}
            onChange={(e) =>
              setVarianceFilter(
                e.target.value as '' | 'positive' | 'zero' | 'negative' | 'nonzero',
              )
            }
          />
        </FilterField>
        <FilterField label={labels.activeOnly}>
          <div className="flex h-8 items-center rounded-lg border border-line bg-surface-card px-3">
            <Switch
              checked={activeOnly}
              onChange={(e) => setActiveOnly(e.target.checked)}
              aria-label={labels.activeOnly}
              size="sm"
            />
          </div>
        </FilterField>
      </ListFiltersBar>

      {error ? (
        <Callout variant="danger" className="shrink-0">
          {error}
        </Callout>
      ) : null}

      {loading || !report ? (
        <LoadingSpinner message={t().common.loading} />
      ) : (
        <>
          <div className="grid shrink-0 gap-4 md:grid-cols-3">
            <SummaryCard
              title={`${labels.expectedBilling} (${report.periodMonths} ${labels.periodMonths.toLowerCase()})`}
              totals={report.totals.expected}
            />
            <SummaryCard
              title={labels.actualBilling}
              totals={report.totals.billed}
            />
            <SummaryCard
              title={labels.variance}
              totals={report.totals.variance}
              emphasize
            />
          </div>

          <DataTablePanel className="min-h-0 min-w-0 flex-1">
            {displayRows.length === 0 ? (
              <ListEmptyState title={t().common.empty} />
            ) : (
              <table className="w-max min-w-full text-sm">
                <thead className={dataTableHeadClass}>
                  <tr>
                    <SortableTh
                      sortKey="customerName"
                      sort={sort}
                      onSort={toggleSort}
                    >
                      {t().common.customer}
                    </SortableTh>
                    <SortableTh
                      sortKey="agreement"
                      sort={sort}
                      onSort={toggleSort}
                      align="right"
                    >
                      {labels.agreementMonthly}
                    </SortableTh>
                    <SortableTh
                      sortKey="expected"
                      sort={sort}
                      onSort={toggleSort}
                      align="right"
                    >
                      {labels.expectedBilling}
                    </SortableTh>
                    <SortableTh
                      sortKey="billed"
                      sort={sort}
                      onSort={toggleSort}
                      align="right"
                    >
                      {labels.actualBilling}
                    </SortableTh>
                    <SortableTh
                      sortKey="varianceInvoice"
                      sort={sort}
                      onSort={toggleSort}
                      align="right"
                    >
                      {t().financial.invoice}
                    </SortableTh>
                    <SortableTh
                      sortKey="varianceCash"
                      sort={sort}
                      onSort={toggleSort}
                      align="right"
                    >
                      {t().financial.cash}
                    </SortableTh>
                    <SortableTh
                      sortKey="variance"
                      sort={sort}
                      onSort={toggleSort}
                      align="right"
                    >
                      {labels.variance}
                    </SortableTh>
                  </tr>
                </thead>
                <tbody>
                  {displayRows.map((row) => (
                    <tr key={row.customerId} className="border-t border-line">
                      <td className="max-w-[14rem] px-3 py-2">
                        <Link
                          to={`/customers/${row.customerId}`}
                          className="block truncate text-brand hover:underline"
                          title={row.customerName}
                        >
                          {row.customerName}
                        </Link>
                        {!row.hasAgreement ? (
                          <Badge variant="secondary" className="mt-1">
                            {labels.noAgreement}
                          </Badge>
                        ) : null}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums">
                        {formatMoney(row.agreement.total)}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums">
                        {formatMoney(row.expected.total)}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums">
                        {formatMoney(row.billed.total)}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums">
                        <VarianceValue value={row.variance.invoice} />
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums">
                        <VarianceValue value={row.variance.cash} />
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums font-medium">
                        <VarianceValue value={row.variance.total} />
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className={dataTableFootClass}>
                  <tr>
                    <td className={totalsCellClass}>{t().common.total}</td>
                    <td className={totalsNumClass}>
                      {formatMoney(report.totals.agreement.total)}
                    </td>
                    <td className={totalsNumClass}>
                      {formatMoney(report.totals.expected.total)}
                    </td>
                    <td className={totalsNumClass}>
                      {formatMoney(report.totals.billed.total)}
                    </td>
                    <td className={totalsNumClass}>
                      <VarianceValue value={report.totals.variance.invoice} />
                    </td>
                    <td className={totalsNumClass}>
                      <VarianceValue value={report.totals.variance.cash} />
                    </td>
                    <td className={totalsNumClass}>
                      <VarianceValue value={report.totals.variance.total} />
                    </td>
                  </tr>
                </tfoot>
              </table>
            )}
          </DataTablePanel>
        </>
      )}
    </PageShell>
  );
}

function VarianceValue({ value }: { value: string }) {
  const n = Number(value);
  const color =
    n > 0 ? 'text-accent' : n < 0 ? 'text-red-600 dark:text-red-400' : '';
  const prefix = n > 0 ? '+' : '';
  return (
    <span className={color}>
      {prefix}
      {formatMoney(value)}
    </span>
  );
}

function SummaryCard({
  title,
  totals,
  emphasize,
}: {
  title: string;
  totals: { invoice: string; cash: string; total: string };
  emphasize?: boolean;
}) {
  return (
    <Card className="p-4 text-sm">
      <div className="font-semibold text-ink">{title}</div>
      <div className="mt-3 space-y-1">
        <div className="flex justify-between">
          <span>{t().financial.invoice}</span>
          <span className="tabular-nums">{formatMoney(totals.invoice)}</span>
        </div>
        <div className="flex justify-between">
          <span>{t().financial.cash}</span>
          <span className="tabular-nums">{formatMoney(totals.cash)}</span>
        </div>
        <div
          className={`flex justify-between font-semibold ${emphasize ? 'text-brand' : ''}`}
        >
          <span>{t().financial.grandTotal}</span>
          <span className="tabular-nums">
            {emphasize ? (
              <VarianceValue value={totals.total} />
            ) : (
              formatMoney(totals.total)
            )}
          </span>
        </div>
      </div>
    </Card>
  );
}
